import { type RefObject, useEffect } from "react";

type useDialogFocusOptions = {
  isOpen: boolean;
  dialogRef: React.RefObject<HTMLElement | null>;
  previouslyFocusedElementRef: RefObject<Element | null>;
};

/**
 * This hook manages focus for a dialog component.
 *
 * When the dialog opens, it focuses the dialog element.
 *
 * When the dialog closes, it restores focus to the previously focused element.
 */
export function useDialogFocus({
  isOpen,
  dialogRef,
  previouslyFocusedElementRef,
}: useDialogFocusOptions) {
  useEffect(() => {
    if (isOpen) {
      previouslyFocusedElementRef.current = document.activeElement;

      setTimeout(() => {
        if (dialogRef.current) {
          dialogRef.current.focus();
        }
      }, 100);
    } else {
      if (
        previouslyFocusedElementRef.current &&
        "focus" in previouslyFocusedElementRef.current
      ) {
        (previouslyFocusedElementRef.current as HTMLElement).focus();
      }
    }
  }, [isOpen, dialogRef, previouslyFocusedElementRef]);
}
